import {
  alpha,
  CSSObject,
  SxProps,
  tablePaginationClasses,
  Theme,
} from '@rossum/ui/material';
import { DataGridProProps, gridClasses } from '@rossum/ui/x-data-grid-pro';

const { columnHeader, cellCheckbox, columnHeaderCheckbox, cell, row } =
  gridClasses;

export const HEADER_COLUMN_HEIGHT = 56;
export const ROW_HEIGHT = 48;

export const transparentBorderStyles = {
  borderColor: 'transparent',

  // custom border on row and remove border on cell is needed to prevent wrong line height calculation
  [`.${row}`]: {
    borderBottom: (t: Theme) => `1px solid ${t.palette.divider}`,
    [`.${cell}`]: {
      border: 'none',
    },
    [`.${gridClasses['cell--pinnedLeft']}`]: {
      borderBottom: (t: Theme) => `1px solid ${t.palette.divider}`,
    },
    [`.${gridClasses['cell--pinnedRight']}`]: {
      borderBottom: (t: Theme) => `1px solid ${t.palette.divider}`,
    },
  },
  [`.${columnHeader}:focus, .${cell}:focus, .${cellCheckbox}:focus-within, .${columnHeaderCheckbox}:focus-within`]:
    {
      outline: 'none',
    },
  [`.${gridClasses['filler--pinnedLeft']}, .${gridClasses['filler--pinnedRight']}, .${gridClasses.filler} > div`]:
    {
      border: 'none',
    },
};

export const clearedCellOutlineStyles = {
  [`&.${gridClasses.root} .${gridClasses.cell}`]: {
    outline: 'none',
  },
};

const transparentBackgroundStyles: SxProps<Theme> = {
  [`
    & .${gridClasses['columnHeader--pinnedRight']},
    & .${gridClasses['cell--pinnedRight']}
  `]: {
    borderLeftColor: 'transparent',
  },
  [`
    & .${gridClasses['columnHeader--pinnedLeft']},
    & .${gridClasses['cell--pinnedLeft']}
  `]: {
    borderRightColor: 'transparent',
  },
  // TODO: Cascade
  // TODO: extract paper styles + pagination - multiple places
  [`
    & .${gridClasses.row}:hover .${gridClasses['cell--pinnedLeft']},
    & .${gridClasses.row}:hover .${gridClasses['cell--pinnedRight']}
  `]: {
    backgroundColor: 'var(--DataGrid-pinnedBackground) !important',
    backgroundImage: t =>
      `linear-gradient(${t.palette.action.hover}, ${t.palette.action.hover})`,
  },
  [`
    & .${gridClasses.row}.Mui-selected .${gridClasses['cell--pinnedLeft']},
    & .${gridClasses.row}.Mui-selected .${gridClasses['cell--pinnedRight']}
  `]: {
    backgroundColor: 'var(--DataGrid-pinnedBackground) !important',
    backgroundImage: t =>
      `linear-gradient(${alpha(t.palette.primary.main, t.palette.action.selectedOpacity)}, ${alpha(t.palette.primary.main, t.palette.action.selectedOpacity)})`,
  },
  [`
    & .${gridClasses.row}.Mui-selected:hover .${gridClasses['cell--pinnedLeft']},
    & .${gridClasses.row}.Mui-selected:hover .${gridClasses['cell--pinnedRight']}
  `]: {
    backgroundColor: 'var(--DataGrid-pinnedBackground) !important',
    backgroundImage: t =>
      `linear-gradient(${alpha(t.palette.primary.main, t.palette.action.hoverOpacity + t.palette.action.selectedOpacity)}, ${alpha(t.palette.primary.main, t.palette.action.hoverOpacity + t.palette.action.selectedOpacity)})`,
  },
  // for handling background of column headers use in slots of the DataGrid this:
  // ./src/ui/data-grid/ColumnHeaderPaper.tsx
  [`& .${gridClasses.columnHeaders}`]: {
    background: 'transparent',
    [`& .${gridClasses['row--borderBottom']}`]: {
      background: 'transparent',
    },
  },
};

export const paginationStyles = {
  [`& .${tablePaginationClasses.selectLabel}`]: {
    margin: 0,
  },
  [`& .${tablePaginationClasses.displayedRows}`]: {
    margin: 0,
  },
};

export const commonDataGridStyles = {
  ...clearedCellOutlineStyles,
  ...transparentBorderStyles,
  ...transparentBackgroundStyles,
  ...paginationStyles,
};

export const getDatagridBackgroundStyles = (color: 'paper' | 'default') =>
  ({
    '&': {
      '--DataGrid-pinnedBackground': t => t.palette.background[color],
      '--DataGrid-containerBackground': t => t.palette.background[color],
    },
  }) as const satisfies DataGridProProps['sx'];

export const absoluteMaxSizeStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
} as const satisfies CSSObject;

export const restrictedRowClass = 'restricted-row';

export const restrictedRowStyles: DataGridProProps['sx'] = {
  [`& .${restrictedRowClass}`]: {
    [`& .${gridClasses.cell}`]: {
      border: 'none',
    },
  },
};
